import { isImmutable } from "immutable";
import { forOwn, isEqual, isFunction, isNil, map } from "lodash";
import { createSelectorCreator } from "reselect";

import { BATCH } from "../modules/chainActionTypes";

/* eslint-disable no-console */

const displayStringForDomNode = node =>
  node ? `${node.nodeName}${node.id ? "#" + node.id : ""}${node.className ? "." + node.className : ""}` : null;

const displayStringForAction = action =>
  action.type === BATCH ? map(action.actions, a => displayStringForAction(a)).join(" | ") : action.type;

const logPropChanges = (className, prevProps, nextProps, showAllChanges = false, verbose = false) => {
  forOwn(nextProps, (nextValue, key) => {
    const prevValue = prevProps[key];
    let warningShown = false;
    if (nextValue !== prevValue) {
      if (isNil(nextValue) && isNil(prevValue)) {
        console.log(`${className}: WARNING: ${key} - changing from nil-type to different nil-type`);
        warningShown = true;
      } else if (isNil(nextValue)) {
        if (showAllChanges) console.log(`${className}: ${key} - changing from non-null to null value`);
      } else if (isNil(prevValue)) {
        if (showAllChanges) console.log(`${className}: ${key} - changing from null to non-null value`);
      } else if (isFunction(nextValue) && isFunction(prevValue)) {
        console.log(`${className}: WARNING: ${key} - changing from function to function`);
        warningShown = true;
      } else if (isImmutable(nextValue) && isImmutable(prevValue)) {
        // Both are immutable
        // Are they the same by value but not reference?
        // Or different by both
        if (isEqual(nextValue.toJS(), prevValue.toJS())) {
          console.log(`${className}: WARNING: ${key} - shares same value but differs by immutable object`);
          warningShown = true;
        } else {
          if (showAllChanges) console.log(`${className}: ${key} - are different immutable values`);
        }
      } else if (isImmutable(nextValue)) {
        console.log(`${className}: WARNING: ${key} - changing from non-immutable to immutable object`);
        warningShown = true;
      } else if (isImmutable(prevValue)) {
        console.log(`${className}: WARNING: ${key} - changing from immutable to non-immutable object`);
        warningShown = true;
      } else {
        if (isEqual(nextValue, prevValue)) {
          console.log(`${className}: WARNING: ${key} - shares same value but differs by object reference`);
          warningShown = true;
        } else {
          if (showAllChanges) console.log(`${className}: ${key} - are different values`);
        }
      }

      if (verbose && (warningShown || showAllChanges)) {
        console.log(`\t\t... nextValue = ${nextValue}, prevValue = ${prevValue}`);
      }
    }
  });
};

function defaultEqualityCheck(a, b, index) {
  if (a !== b) {
    if (isImmutable(a) && isImmutable(b)) {
      // Both are immutable
      // Are they the same by value but not reference?
      // Or different by both
      if (isEqual(a.toJS(), b.toJS())) {
        console.log(`debugReselect(): parameter[${index}] shares same value but differ by immutable object:`);
        console.dirJs(b);
      } else {
        console.log(`debugReselect(): parameter[${index}] are different immutable values`);
      }
    } else {
      console.log(`debugReselect(): parameter[${index}] not equal for old = ${a}, new = ${b}`);
    }
  }
  return a === b;
}
function areArgumentsShallowlyEqual(equalityCheck, prev, next) {
  if (prev === null || next === null || prev.length !== next.length) {
    console.log(`debugReselect(): bailing for null prev or next, or because length changed... printing prev, next`);
    console.dir(prev);
    console.dir(next);
    return false;
  }

  // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.
  const length = prev.length;
  for (let i = 0; i < length; i++) {
    if (!equalityCheck(prev[i], next[i], i)) {
      return false;
    }
  }

  return true;
}
function defaultMemoize(func, equalityCheck = defaultEqualityCheck) {
  let lastArgs = null;
  let lastResult = null;
  // we reference arguments instead of spreading them for performance reasons
  return function() {
    if (!areArgumentsShallowlyEqual(equalityCheck, lastArgs, arguments)) {
      // apply arguments instead of spreading for performance.
      lastResult = func.apply(null, arguments);
    }

    lastArgs = arguments;
    return lastResult;
  };
}

/**
 * Call this instead of standard reselect `createSelector`
 */
const debugCreateSelector = createSelectorCreator(defaultMemoize);

/**
 * USAGE:
 * 
 * 
   const someFunc = createCachedSelector(
      // inputs
      a => a,
      // output
      a => {
        return a.b;
      }
   )(a => a.id, debugUtils.debugCacheSelectorOptions);
*/
const debugCacheSelectorOptions = {
  selectorCreator: debugCreateSelector
};

/* eslint-enable no-console */

export default {
  logPropChanges,
  debugCreateSelector,
  debugCacheSelectorOptions,
  displayStringForDomNode,
  displayStringForAction
};
